import React, { useState, useRef, useEffect } from 'react';
import { 
  Maximize2, 
  Minimize2, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Trash2,
  Move,
  X
} from 'lucide-react';

const ImageEditor = ({ image, onUpdate, onDelete, onClose }) => {
  const [width, setWidth] = useState(image.width || 300);
  const [height, setHeight] = useState(image.height || 'auto');
  const [alignment, setAlignment] = useState(image.align || 'left');
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);

  const handleResize = (newWidth) => {
    setWidth(newWidth);
    if (imageRef.current) {
      imageRef.current.style.width = `${newWidth}px`;
    }
  };

  const handleAlignment = (align) => {
    setAlignment(align);
    if (imageRef.current) {
      imageRef.current.style.float = align === 'left' ? 'left' : align === 'right' ? 'right' : 'none';
      imageRef.current.style.display = align === 'center' ? 'block' : 'inline';
      imageRef.current.style.margin = align === 'center' ? '1rem auto' : '0 1rem 1rem 0';
    }
  };

  const handleSave = () => {
    onUpdate({
      width,
      height,
      alignment
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-primary">Edit Image</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Image Preview */}
        <div className="p-6 bg-gray-50">
          <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 text-center">
            <img
              ref={imageRef}
              src={image.src}
              alt="Preview"
              style={{
                width: `${width}px`,
                height: height === 'auto' ? 'auto' : `${height}px`,
                maxWidth: '100%',
                display: alignment === 'center' ? 'block' : 'inline',
                margin: alignment === 'center' ? '0 auto' : '0',
                float: alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'none'
              }}
              className="rounded-lg shadow-md"
            />
          </div>
        </div>

        {/* Controls */}
        <div className="p-6 space-y-6">
          {/* Size Controls */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">Image Size</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleResize(200)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  width === 200 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Small (200px)
              </button>
              <button
                onClick={() => handleResize(400)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  width === 400 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Medium (400px)
              </button>
              <button
                onClick={() => handleResize(600)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                  width === 600 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Large (600px)
              </button>
            </div>

            {/* Custom Size Slider */}
            <div className="mt-4">
              <label className="block text-sm text-gray-600 mb-2">Custom Width: {width}px</label>
              <input
                type="range"
                min="100"
                max="800"
                value={width}
                onChange={(e) => handleResize(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
          </div>

          {/* Alignment Controls */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">Image Alignment</label>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => handleAlignment('left')}
                className={`px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  alignment === 'left' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <AlignLeft className="w-5 h-5" />
                Left
              </button>
              <button
                onClick={() => handleAlignment('center')}
                className={`px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  alignment === 'center' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <AlignCenter className="w-5 h-5" />
                Center
              </button>
              <button
                onClick={() => handleAlignment('right')}
                className={`px-4 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  alignment === 'right' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <AlignRight className="w-5 h-5" />
                Right
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="text-sm font-bold text-blue-900 mb-2">💡 Quick Tips:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Use slider for precise width control</li>
              <li>• Left/Right alignment makes text wrap around image</li>
              <li>• Center alignment places image on its own line</li>
              <li>• Small = 200px, Medium = 400px, Large = 600px</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              className="flex-1 bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Apply Changes
            </button>
            <button
              onClick={onDelete}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-5 h-5" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
